import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { TrendingUp, DollarSign, BarChart3, PieChart, FileText, FileSpreadsheet, TrendingDown, Minus } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RePieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['hsl(262.1 83.3% 57.8%)', 'hsl(142.1 76.2% 36.3%)', 'hsl(199 89% 48%)', 'hsl(47.9 95.8% 53.1%)', 'hsl(346.8 77.2% 49.8%)'];

// Helper to get date one month ago
const getOneMonthAgo = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return date.toISOString().split('T')[0];
};

const getToday = () => {
  return new Date().toISOString().split('T')[0];
};

export default function AnalyticsPage() {
  const [approvalTrends, setApprovalTrends] = useState({ fund_requests: [], material_requests: [] });
  const [expensePatterns, setExpensePatterns] = useState({ by_supervisor: [], by_category: {} });
  const [approvalRates, setApprovalRates] = useState({});
  const [budgetUtilization, setBudgetUtilization] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // P&L State
  const [pnlData, setPnlData] = useState({ summary: {}, data: [] });
  const [pnlLoading, setPnlLoading] = useState(false);
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [quoteNoFilter, setQuoteNoFilter] = useState('');
  
  // Export Dialog State
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    quote_no: '',
    from_date: getOneMonthAgo(),
    to_date: getToday()
  });

  useEffect(() => {
    fetchAnalytics();
    // Auto-load P&L with no date filter to show all quotes
    fetchProfitLoss({ quote_no: '', from_date: '', to_date: '' });
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [trendsRes, patternsRes, ratesRes, budgetRes] = await Promise.all([
        axios.get(`${API}/analytics/approval-trends`),
        axios.get(`${API}/analytics/expense-patterns`),
        axios.get(`${API}/analytics/approval-rates`),
        axios.get(`${API}/analytics/budget-utilization`)
      ]);

      setApprovalTrends(trendsRes.data);
      setExpensePatterns(patternsRes.data);
      setApprovalRates(ratesRes.data);
      setBudgetUtilization(budgetRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfitLoss = async (filters = null) => {
    setPnlLoading(true);
    try {
      const params = new URLSearchParams();
      const activeFilters = filters || exportFilters;
      if (activeFilters.quote_no) params.append('quote_no', activeFilters.quote_no);
      if (activeFilters.from_date) params.append('from_date', activeFilters.from_date);
      if (activeFilters.to_date) params.append('to_date', activeFilters.to_date);
      
      const response = await axios.get(`${API}/analytics/profit-loss?${params.toString()}`);
      setPnlData(response.data);
    } catch (error) {
      toast.error('Failed to load P&L data');
      console.error(error);
    } finally {
      setPnlLoading(false);
    }
  };

  const handleExportFilterChange = (field, value) => {
    setExportFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleExportOk = async () => {
    setPnlLoading(true);
    try {
      const params = new URLSearchParams();
      if (exportFilters.quote_no) params.append('quote_no', exportFilters.quote_no);
      if (exportFilters.from_date) params.append('from_date', exportFilters.from_date);
      if (exportFilters.to_date) params.append('to_date', exportFilters.to_date);
      
      // Download Excel file
      const response = await axios.get(`${API}/analytics/profit-loss/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PnL_Statement_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('P&L Report exported successfully');
      setExportDialogOpen(false);
      
      // Also update the displayed data with the same filters
      fetchProfitLoss(exportFilters);
    } catch (error) {
      toast.error('Failed to export P&L report');
      console.error(error);
    } finally {
      setPnlLoading(false);
    }
  };

  const resetExportFilters = () => {
    setExportFilters({
      quote_no: '',
      from_date: getOneMonthAgo(),
      to_date: getToday()
    });
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card className="p-6 glass-card">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        <div
          className="p-3 rounded-lg"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="h-6 w-6" style={{ color }} />
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-lg">Loading analytics...</div>
        </div>
      </Sidebar>
    );
  }

  const categoryPieData = [
    { name: 'Fund Requests', value: expensePatterns.by_category.total_fund_amount || 0 },
    { name: 'Material Requests', value: expensePatterns.by_category.total_material_amount || 0 }
  ];

  return (
    <Sidebar>
      <div className="space-y-8" data-testid="analytics-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <BarChart3 className="h-8 w-8" />
          <div>
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Financial Analytics
            </h1>
            <p className="text-muted-foreground mt-2">
              Data-driven insights for budget optimization and decision making
            </p>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Fund Approval Rate"
            value={`${approvalRates.fund_requests?.approval_rate || 0}%`}
            icon={TrendingUp}
            color="hsl(142.1 76.2% 36.3%)"
            subtitle={`${approvalRates.fund_requests?.approved || 0}/${approvalRates.fund_requests?.total || 0} approved`}
          />
          <StatCard
            title="Material Approval Rate"
            value={`${approvalRates.material_requests?.approval_rate || 0}%`}
            icon={TrendingUp}
            color="hsl(199 89% 48%)"
            subtitle={`${approvalRates.material_requests?.approved || 0}/${approvalRates.material_requests?.total || 0} approved`}
          />
          <StatCard
            title="Total Expenses"
            value={`₹${((expensePatterns.by_category.total_fund_amount || 0) + (expensePatterns.by_category.total_material_amount || 0)).toLocaleString('en-IN')}`}
            icon={DollarSign}
            color="hsl(262.1 83.3% 57.8%)"
            subtitle="Approved amount"
          />
          <StatCard
            title="Avg. Approval Time"
            value={`${approvalRates.avg_approval_days || 0} days`}
            icon={BarChart3}
            color="hsl(47.9 95.8% 53.1%)"
            subtitle="From request to approval"
          />
        </div>

        {/* Approval Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6 glass-card">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Fund Request Trends (Last 30 Days)
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={approvalTrends.fund_requests}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="_id"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="hsl(262.1 83.3% 57.8%)"
                  strokeWidth={2}
                  name="Total Requests"
                />
                <Line
                  type="monotone"
                  dataKey="approved"
                  stroke="hsl(142.1 76.2% 36.3%)"
                  strokeWidth={2}
                  name="Approved"
                />
                <Line
                  type="monotone"
                  dataKey="pending"
                  stroke="hsl(47.9 95.8% 53.1%)"
                  strokeWidth={2}
                  name="Pending"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-6 glass-card">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Material Request Trends (Last 30 Days)
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={approvalTrends.material_requests}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="_id"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="hsl(199 89% 48%)"
                  strokeWidth={2}
                  name="Total Requests"
                />
                <Line
                  type="monotone"
                  dataKey="approved"
                  stroke="hsl(142.1 76.2% 36.3%)"
                  strokeWidth={2}
                  name="Approved"
                />
                <Line
                  type="monotone"
                  dataKey="pending"
                  stroke="hsl(47.9 95.8% 53.1%)"
                  strokeWidth={2}
                  name="Pending"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* Expense Patterns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6 glass-card">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Expenses by Supervisor
            </h2>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={expensePatterns.by_supervisor.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="_id"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => `₹${value.toLocaleString('en-IN')}`}
                />
                <Legend />
                <Bar dataKey="total_requested" fill="hsl(199 89% 48%)" name="Requested" />
                <Bar dataKey="total_approved" fill="hsl(142.1 76.2% 36.3%)" name="Approved" />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-6 glass-card">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Expense Category Breakdown
            </h2>
            <ResponsiveContainer width="100%" height={350}>
              <RePieChart>
                <Pie
                  data={categoryPieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => `₹${value.toLocaleString('en-IN')}`}
                />
              </RePieChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Fund Requests</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  ₹{(expensePatterns.by_category.total_fund_amount || 0).toLocaleString('en-IN')}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Material Requests</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  ₹{(expensePatterns.by_category.total_material_amount || 0).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Budget Utilization */}
        <Card className="p-6 glass-card">
          <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Budget Utilization by Quote
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium">Quote No</th>
                  <th className="px-6 py-4 text-left text-sm font-medium">Supervisor</th>
                  <th className="px-6 py-4 text-right text-sm font-medium">Quote Amount</th>
                  <th className="px-6 py-4 text-right text-sm font-medium">Approved Amount</th>
                  <th className="px-6 py-4 text-right text-sm font-medium">Utilization %</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {budgetUtilization.slice(0, 10).map((item, index) => (
                  <tr key={index} className="hover:bg-muted/50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm font-medium">{item.quote_no}</span>
                    </td>
                    <td className="px-6 py-4 text-sm">{item.supervisor_name}</td>
                    <td className="px-6 py-4 text-sm text-right font-mono">
                      ₹{item.amount_with_management_fee?.toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-mono">
                      ₹{(item.total_approved || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Badge
                        className={
                          item.utilization_percentage > 90
                            ? 'bg-red-100 text-red-800'
                            : item.utilization_percentage > 70
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }
                      >
                        {item.utilization_percentage?.toFixed(1)}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Supervisor Performance */}
        <Card className="p-6 glass-card">
          <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Supervisor Performance Metrics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {expensePatterns.by_supervisor.slice(0, 6).map((supervisor, index) => (
              <Card key={index} className="p-4 bg-muted/30">
                <p className="font-medium mb-2">{supervisor._id}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Requests:</span>
                    <span className="font-mono">{supervisor.count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Requested:</span>
                    <span className="font-mono">₹{supervisor.total_requested?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Approved:</span>
                    <span className="font-mono font-semibold text-green-600">
                      ₹{supervisor.total_approved?.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-muted-foreground">Approval Rate:</span>
                    <Badge variant="secondary">
                      {((supervisor.total_approved / supervisor.total_requested) * 100).toFixed(1)}%
                    </Badge>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Card>

        {/* Profit & Loss Statement Section */}
        <Card className="p-6 glass-card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <FileText className="h-6 w-6 text-[#DC143C]" />
              <h3 className="text-xl font-bold">Profit & Loss Statement</h3>
            </div>
            
            {/* Export to Excel Dialog */}
            <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-green-600 text-green-700 hover:bg-green-50"
                  data-testid="pnl-export-btn"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export to Excel
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Export P&L Report</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="export_quote_no">Quote Number</Label>
                    <Input
                      id="export_quote_no"
                      placeholder="All quotes (leave empty for all)"
                      value={exportFilters.quote_no}
                      onChange={(e) => handleExportFilterChange('quote_no', e.target.value)}
                      data-testid="export-quote-filter"
                    />
                    <p className="text-xs text-muted-foreground">Leave empty to include all quotes</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="export_from_date">From Date</Label>
                      <Input
                        id="export_from_date"
                        type="date"
                        value={exportFilters.from_date}
                        onChange={(e) => handleExportFilterChange('from_date', e.target.value)}
                        data-testid="export-from-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="export_to_date">To Date</Label>
                      <Input
                        id="export_to_date"
                        type="date"
                        value={exportFilters.to_date}
                        onChange={(e) => handleExportFilterChange('to_date', e.target.value)}
                        data-testid="export-to-date"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={resetExportFilters}>
                      Reset
                    </Button>
                    <Button 
                      onClick={handleExportOk}
                      disabled={pnlLoading}
                      className="bg-[#DC143C] hover:bg-[#b91130]"
                    >
                      {pnlLoading ? 'Loading...' : 'OK'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* P&L Summary */}
          {pnlData.summary && pnlData.data?.length > 0 && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
                <Card className="p-3 text-center bg-blue-50">
                  <p className="text-xs text-muted-foreground">Total Quotes</p>
                  <p className="text-xl font-bold text-blue-700">{pnlData.summary.total_quotes}</p>
                </Card>
                <Card className="p-3 text-center bg-green-50">
                  <p className="text-xs text-muted-foreground">Total Revenue</p>
                  <p className="text-sm font-bold text-green-700">₹{pnlData.summary.total_revenue?.toLocaleString('en-IN')}</p>
                </Card>
                <Card className="p-3 text-center bg-red-50">
                  <p className="text-xs text-muted-foreground">Total Expenditure</p>
                  <p className="text-sm font-bold text-red-700">₹{pnlData.summary.total_expenditure?.toLocaleString('en-IN')}</p>
                </Card>
                <Card className={`p-3 text-center ${pnlData.summary.total_profit_loss >= 0 ? 'bg-emerald-50' : 'bg-orange-50'}`}>
                  <p className="text-xs text-muted-foreground">Net Profit/Loss</p>
                  <p className={`text-sm font-bold ${pnlData.summary.total_profit_loss >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>
                    ₹{pnlData.summary.total_profit_loss?.toLocaleString('en-IN')}
                  </p>
                </Card>
                <Card className="p-3 text-center bg-green-50">
                  <p className="text-xs text-muted-foreground">Profitable</p>
                  <p className="text-xl font-bold text-green-700">{pnlData.summary.profitable_quotes}</p>
                </Card>
                <Card className="p-3 text-center bg-red-50">
                  <p className="text-xs text-muted-foreground">Loss Making</p>
                  <p className="text-xl font-bold text-red-700">{pnlData.summary.loss_making_quotes}</p>
                </Card>
              </div>

              {/* P&L Table - Scrollable with max 5 visible rows */}
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[320px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-[#1B273D] text-white">
                        <th className="px-3 py-2 text-left">
                          <div className="space-y-1">
                            <span>Quote No</span>
                            <Input
                              type="text"
                              placeholder="Filter..."
                              value={quoteNoFilter}
                              onChange={(e) => setQuoteNoFilter(e.target.value)}
                              className="h-6 text-xs bg-white/90 text-gray-800 placeholder:text-gray-400 border-0 focus-visible:ring-1 focus-visible:ring-white"
                              data-testid="pnl-quote-filter"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </th>
                        <th className="px-3 py-2 text-left">Supervisor</th>
                        <th className="px-3 py-2 text-right">Quote Amt</th>
                        <th className="px-3 py-2 text-right">Fund Req</th>
                        <th className="px-3 py-2 text-right">Material Req</th>
                        <th className="px-3 py-2 text-right">Total Exp</th>
                        <th className="px-3 py-2 text-right">P/L</th>
                        <th className="px-3 py-2 text-center">Margin</th>
                        <th className="px-3 py-2 text-center">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y bg-white">
                      {pnlData.data
                        .filter(item => !quoteNoFilter || item.quote_no?.toLowerCase().includes(quoteNoFilter.toLowerCase()))
                        .map((item) => (
                        <React.Fragment key={item.quote_id}>
                          <tr className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono text-[#DC143C] font-medium">{item.quote_no}</td>
                            <td className="px-3 py-2 truncate max-w-[100px]" title={item.supervisor_name}>{item.supervisor_name}</td>
                            <td className="px-3 py-2 text-right font-mono">₹{item.quote_amount?.toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-right font-mono text-red-600">₹{item.fund_request_total?.toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-right font-mono text-red-600">₹{item.material_request_total?.toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-right font-mono font-semibold text-red-700">₹{item.total_expenditure?.toLocaleString('en-IN')}</td>
                            <td className={`px-3 py-2 text-right font-mono font-bold ${item.is_profit ? 'text-green-600' : 'text-orange-600'}`}>
                              <span className="inline-flex items-center gap-1">
                                {item.is_profit ? <TrendingUp className="h-3 w-3" /> : item.profit_loss === 0 ? <Minus className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                                ₹{Math.abs(item.profit_loss)?.toLocaleString('en-IN')}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Badge className={`text-xs ${item.is_profit ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {item.profit_margin?.toFixed(1)}%
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-xs"
                                onClick={() => setExpandedQuote(expandedQuote === item.quote_id ? null : item.quote_id)}
                              >
                                {expandedQuote === item.quote_id ? 'Hide' : 'View'}
                              </Button>
                            </td>
                          </tr>
                          {expandedQuote === item.quote_id && (
                            <tr>
                              <td colSpan={9} className="bg-gray-50 p-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {/* Fund Request Details */}
                                  <div className="bg-white p-3 rounded-lg border">
                                    <h4 className="font-semibold mb-2 text-xs text-blue-700">Fund Requests ({item.fund_details?.length || 0})</h4>
                                    {item.fund_details?.length > 0 ? (
                                      <div className="space-y-1 max-h-32 overflow-y-auto">
                                        {item.fund_details.map((fd, idx) => (
                                          <div key={idx} className="text-xs p-2 bg-gray-50 rounded flex justify-between">
                                            <div>
                                              <Badge variant="outline" className="text-xs">{fd.status}</Badge>
                                              {fd.remarks && <p className="text-muted-foreground mt-1 text-xs">{fd.remarks}</p>}
                                            </div>
                                            <div className="text-right">
                                              <p>Req: ₹{fd.requested_amount?.toLocaleString('en-IN')}</p>
                                              <p className="font-semibold text-green-600">Appr: ₹{fd.approved_amount?.toLocaleString('en-IN')}</p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-muted-foreground">No fund requests</p>
                                    )}
                                  </div>

                                  {/* Material Request Details */}
                                  <div className="bg-white p-3 rounded-lg border">
                                    <h4 className="font-semibold mb-2 text-xs text-purple-700">Material Requests ({item.material_details?.length || 0})</h4>
                                    {item.material_details?.length > 0 ? (
                                      <div className="space-y-1 max-h-32 overflow-y-auto">
                                        {item.material_details.map((md, idx) => (
                                          <div key={idx} className="text-xs p-2 bg-gray-50 rounded flex justify-between">
                                            <div>
                                              <Badge variant="outline" className="text-xs">{md.status}</Badge>
                                              <p className="text-muted-foreground mt-1 text-xs truncate max-w-[120px]">
                                                {md.materials?.map(m => m.name).join(', ')}
                                              </p>
                                            </div>
                                            <div className="text-right">
                                              <p>DM: ₹{md.dm_approved_amount?.toLocaleString('en-IN')}</p>
                                              <p className="font-semibold text-green-600">PH: ₹{md.ph_approved_amount?.toLocaleString('en-IN')}</p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-muted-foreground">No material requests</p>
                                    )}
                                  </div>

                                  {/* Billing Details */}
                                  {item.billing_details?.length > 0 && (
                                    <div className="bg-white p-3 rounded-lg border md:col-span-2">
                                      <h4 className="font-semibold mb-2 text-xs text-orange-700">Billing References ({item.billing_details?.length || 0})</h4>
                                      <div className="space-y-1">
                                        {item.billing_details.map((bd, idx) => (
                                          <div key={idx} className="text-xs p-2 bg-gray-50 rounded flex justify-between">
                                            <div>
                                              <span className="font-mono font-medium">{bd.jms_no}</span>
                                              <Badge variant="outline" className="text-xs ml-2">{bd.status}</Badge>
                                            </div>
                                            <span className="font-semibold">₹{bd.amount?.toLocaleString('en-IN')}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {!pnlData.data?.length && !pnlLoading && (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Click "Export to Excel" to generate report with filters</p>
            </div>
          )}
          
          {pnlLoading && (
            <div className="text-center py-8 text-muted-foreground">
              <p>Loading P&L data...</p>
            </div>
          )}
        </Card>
      </div>
    </Sidebar>
  );
}
